import { useEffect, useMemo, useState } from 'react';
import {
  useFeedbackStore,
  useBrowseFeedback,
  useWinners,
  type FeedbackCategory,
  type FeedbackItem,
  type FeedbackSort,
} from '@/state/feedbackStore';
import { useAuthStore } from '@/state/authStore';
import { useGameStore } from '@/state/gameStore';
import './FeedbackScreen.css';

type SubTab = 'voting' | 'browse' | 'submit';

const CATEGORY_LABEL: Record<FeedbackCategory, string> = {
  feature: '✨ Feature',
  balance: '⚖ Balance',
  bug: '🐛 Bug',
  other: '💭 Sonstiges',
};

const CATEGORY_COLOR: Record<FeedbackCategory, string> = {
  feature: '#4aa7ff',
  balance: '#ff9a2b',
  bug: '#ff6b6b',
  other: '#a4b8c8',
};

const SUBMIT_MIN_LEVEL = 2;

function categoryChip(cat: FeedbackCategory) {
  return (
    <span className="fb-cat-chip" style={{ '--cat-color': CATEGORY_COLOR[cat] } as React.CSSProperties}>
      {CATEGORY_LABEL[cat]}
    </span>
  );
}

function timeAgo(ts: number): string {
  const seconds = Math.floor((Date.now() - ts) / 1000);
  if (seconds < 60) return 'gerade eben';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `vor ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `vor ${days} t`;
  const months = Math.floor(days / 30);
  return `vor ${months} mo`;
}

function countdown(endMs: number): string {
  const total = Math.max(0, endMs - Date.now());
  const days = Math.floor(total / (1000 * 60 * 60 * 24));
  const hrs = Math.floor((total / (1000 * 60 * 60)) % 24);
  const min = Math.floor((total / (1000 * 60)) % 60);
  if (total === 0) return 'beendet';
  if (days > 0) return `${days}t ${hrs}h`;
  if (hrs > 0) return `${hrs}h ${min}m`;
  return `${min}m`;
}

export function FeedbackScreen() {
  const userId = useAuthStore((s) => s.user?.id);
  const playerLevel = useGameStore((s) => s.player?.level ?? 1);

  const loadAll = useFeedbackStore((s) => s.loadAll);
  const loading = useFeedbackStore((s) => s.loading);
  const error = useFeedbackStore((s) => s.error);
  const items = useFeedbackStore((s) => s.items);
  const myUpvoteIds = useFeedbackStore((s) => s.myUpvoteIds);
  const toggleUpvote = useFeedbackStore((s) => s.toggleUpvote);

  const activeRound = useFeedbackStore((s) => s.activeRound);
  const roundCandidates = useFeedbackStore((s) => s.roundCandidates);
  const roundTallies = useFeedbackStore((s) => s.roundTallies);
  const myRoundVote = useFeedbackStore((s) => s.myRoundVote);
  const castRoundVote = useFeedbackStore((s) => s.castRoundVote);

  const sort = useFeedbackStore((s) => s.sort);
  const setSort = useFeedbackStore((s) => s.setSort);
  const filterCategory = useFeedbackStore((s) => s.filterCategory);
  const setFilterCategory = useFeedbackStore((s) => s.setFilterCategory);

  const browseList = useBrowseFeedback();
  const winners = useWinners();

  const submit = useFeedbackStore((s) => s.submit);

  const [tab, setTab] = useState<SubTab>(activeRound ? 'voting' : 'browse');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ ok: boolean; msg: string } | null>(null);

  // Submit form state
  const [draftCat, setDraftCat] = useState<FeedbackCategory>('feature');
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Auto-select voting tab if a round becomes active after load
  useEffect(() => {
    if (activeRound && tab === 'browse' && roundCandidates.length > 0) {
      setTab('voting');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRound?.id]);

  const showFlash = (ok: boolean, msg: string) => {
    setFlash({ ok, msg });
    setTimeout(() => setFlash(null), 3500);
  };

  const handleSubmit = async () => {
    if (busy) return;
    if (!userId) {
      showFlash(false, 'Bitte erst anmelden.');
      return;
    }
    if (playerLevel < SUBMIT_MIN_LEVEL) {
      showFlash(false, `Mindestens Level ${SUBMIT_MIN_LEVEL} erforderlich.`);
      return;
    }
    if (draftTitle.trim().length < 4 || draftTitle.trim().length > 80) {
      showFlash(false, 'Titel muss 4–80 Zeichen lang sein.');
      return;
    }
    if (draftBody.trim().length < 10 || draftBody.trim().length > 1000) {
      showFlash(false, 'Beschreibung muss 10–1000 Zeichen lang sein.');
      return;
    }
    setBusy(true);
    const result = await submit({
      category: draftCat,
      title: draftTitle,
      body: draftBody,
    });
    setBusy(false);
    if (result.ok) {
      showFlash(true, '✓ Idee eingereicht!');
      setDraftTitle('');
      setDraftBody('');
      setTab('browse');
    } else {
      const msg = result.error?.includes('rate_limit')
        ? 'Limit erreicht: max 3 Einreichungen / 24h.'
        : `Fehler: ${result.error}`;
      showFlash(false, msg);
    }
  };

  const handleVote = async (fbId: string) => {
    if (busy || myRoundVote) return;
    setBusy(true);
    const result = await castRoundVote(fbId);
    setBusy(false);
    if (!result.ok) {
      showFlash(false, `Stimme nicht gezählt: ${result.error}`);
    } else {
      showFlash(true, '✓ Stimme abgegeben!');
    }
  };

  const totalRoundVotes = useMemo(
    () => Object.values(roundTallies).reduce((s, n) => s + n, 0),
    [roundTallies],
  );

  return (
    <div className="screen feedback-screen">
      <h1 className="screen__headline">💬 Community-Ideen</h1>

      {/* Sub-Tabs */}
      <div className="fb-tabs">
        <button
          className={`fb-tab ${tab === 'voting' ? 'fb-tab--active' : ''}`}
          onClick={() => setTab('voting')}
        >
          🗳 Abstimmung
          {activeRound && <span className="fb-tab__badge">aktiv</span>}
        </button>
        <button
          className={`fb-tab ${tab === 'browse' ? 'fb-tab--active' : ''}`}
          onClick={() => setTab('browse')}
        >
          📋 Vorschläge ({items.length})
        </button>
        <button
          className={`fb-tab ${tab === 'submit' ? 'fb-tab--active' : ''}`}
          onClick={() => setTab('submit')}
        >
          ➕ Einreichen
        </button>
      </div>

      {flash && (
        <div className={`fb-flash ${flash.ok ? 'fb-flash--ok' : 'fb-flash--err'}`}>
          {flash.msg}
        </div>
      )}
      {error && <div className="fb-flash fb-flash--err">⛔ {error}</div>}

      {/* TAB: VOTING */}
      {tab === 'voting' && (
        <div className="fb-section">
          {!activeRound && (
            <div className="fb-empty">
              Aktuell läuft <strong>keine Abstimmung</strong>. Reiche Vorschläge ein und upvote
              die besten — Top-Ideen kommen in die nächste Runde!
            </div>
          )}
          {activeRound && (
            <>
              <div className="fb-round-header">
                <h2 className="fb-round-title">{activeRound.title}</h2>
                {activeRound.description && (
                  <p className="fb-round-desc">{activeRound.description}</p>
                )}
                <div className="fb-round-meta">
                  <span>⏰ Endet in: <strong>{countdown(activeRound.endsAt)}</strong></span>
                  <span>· {totalRoundVotes} Stimmen abgegeben</span>
                </div>
                {myRoundVote && (
                  <div className="fb-round-voted">
                    ✓ Du hast abgestimmt. Danke fürs Mitgestalten!
                  </div>
                )}
              </div>

              {roundCandidates.length === 0 ? (
                <div className="fb-empty">
                  Keine Kandidaten in dieser Runde.
                </div>
              ) : (
                <div className="fb-list">
                  {roundCandidates.map((c) => {
                    const votes = roundTallies[c.id] ?? 0;
                    const pct = totalRoundVotes > 0
                      ? Math.round((votes / totalRoundVotes) * 100)
                      : 0;
                    const isMyVote = myRoundVote === c.id;
                    const disabled = !!myRoundVote || busy;
                    return (
                      <div
                        key={c.id}
                        className={`fb-vote-card ${isMyVote ? 'fb-vote-card--mine' : ''}`}
                      >
                        <div className="fb-vote-card__head">
                          {categoryChip(c.category)}
                          <h3 className="fb-vote-card__title">{c.title}</h3>
                        </div>
                        <p className="fb-vote-card__body">{c.body}</p>
                        <div className="fb-vote-bar">
                          <div className="fb-vote-bar__fill" style={{ width: `${pct}%` }} />
                          <span className="fb-vote-bar__label">
                            {votes} {votes === 1 ? 'Stimme' : 'Stimmen'} · {pct}%
                          </span>
                        </div>
                        <div className="fb-vote-card__foot">
                          <span className="fb-vote-card__author">— {c.authorName}</span>
                          <button
                            className={`btn ${isMyVote ? 'btn--ghost' : 'btn--primary'}`}
                            disabled={disabled}
                            onClick={() => handleVote(c.id)}
                          >
                            {isMyVote ? '✓ Deine Stimme' : myRoundVote ? 'Bereits gewählt' : 'Wählen'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {winners.length > 0 && (
            <div className="fb-winners">
              <h3 className="fb-winners__title">🏆 Bisherige Gewinner</h3>
              <div className="fb-winners__list">
                {winners.map((w) => (
                  <div key={w.id} className="fb-winner-card">
                    <div className="fb-winner-card__head">
                      🏆 {categoryChip(w.category)}
                      <span className="fb-winner-card__author">— {w.authorName}</span>
                    </div>
                    <div className="fb-winner-card__title">{w.title}</div>
                    <div className="fb-winner-card__body">{w.body}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: BROWSE */}
      {tab === 'browse' && (
        <div className="fb-section">
          <div className="fb-filters">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as FeedbackSort)}
            >
              <option value="top">Beliebteste</option>
              <option value="newest">Neueste</option>
            </select>
            <select
              value={filterCategory ?? ''}
              onChange={(e) =>
                setFilterCategory((e.target.value || null) as FeedbackCategory | null)
              }
            >
              <option value="">Alle Kategorien</option>
              {(Object.keys(CATEGORY_LABEL) as FeedbackCategory[]).map((c) => (
                <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
              ))}
            </select>
          </div>

          {loading && <div className="fb-empty">Lade …</div>}
          {!loading && browseList.length === 0 && (
            <div className="fb-empty">
              Noch keine Vorschläge — sei der Erste, reiche eine Idee ein!
            </div>
          )}

          <div className="fb-list">
            {browseList.map((it) => (
              <FeedbackCard
                key={it.id}
                item={it}
                upvoted={myUpvoteIds.has(it.id)}
                onToggle={() => void toggleUpvote(it.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* TAB: SUBMIT */}
      {tab === 'submit' && (
        <div className="fb-section fb-submit">
          {playerLevel < SUBMIT_MIN_LEVEL ? (
            <div className="fb-locked">
              🔒 Einreichen ab Level <strong>{SUBMIT_MIN_LEVEL}</strong> möglich.
              Spiel ein bisschen, dann kannst du Ideen teilen!
            </div>
          ) : (
            <>
              <p className="fb-submit__intro">
                <strong>Was würdest du am Spiel ändern?</strong> Reiche eine konkrete Idee ein.
                Andere Runner können upvoten — Top-Ideen kommen in die nächste Voting-Runde.
              </p>

              <label className="fb-field">
                <span>Kategorie</span>
                <div className="fb-cat-row">
                  {(Object.keys(CATEGORY_LABEL) as FeedbackCategory[]).map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`fb-cat-btn ${draftCat === c ? 'fb-cat-btn--active' : ''}`}
                      style={{ '--cat-color': CATEGORY_COLOR[c] } as React.CSSProperties}
                      onClick={() => setDraftCat(c)}
                    >
                      {CATEGORY_LABEL[c]}
                    </button>
                  ))}
                </div>
              </label>

              <label className="fb-field">
                <span>
                  Titel <em>({draftTitle.trim().length}/80)</em>
                </span>
                <input
                  type="text"
                  maxLength={80}
                  placeholder="Kurz und knackig — z. B. 'Mehr Boss-Loot in Tier 5'"
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                />
              </label>

              <label className="fb-field">
                <span>
                  Beschreibung <em>({draftBody.trim().length}/1000)</em>
                </span>
                <textarea
                  rows={6}
                  maxLength={1000}
                  placeholder="Was genau willst du sehen? Wie soll's funktionieren? Warum ist das wichtig?"
                  value={draftBody}
                  onChange={(e) => setDraftBody(e.target.value)}
                />
              </label>

              <div className="fb-submit__actions">
                <button
                  className="btn btn--primary"
                  onClick={handleSubmit}
                  disabled={busy}
                >
                  {busy ? 'Sende …' : '➤ Einreichen'}
                </button>
                <span className="fb-submit__hint">
                  Limit: max 3 Einreichungen / 24h.
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface FeedbackCardProps {
  item: FeedbackItem;
  upvoted: boolean;
  onToggle: () => void;
}

function FeedbackCard({ item, upvoted, onToggle }: FeedbackCardProps) {
  const inShortlist = item.status === 'shortlist';
  return (
    <div className={`fb-card ${inShortlist ? 'fb-card--shortlist' : ''}`}>
      <button
        className={`fb-upvote ${upvoted ? 'fb-upvote--active' : ''}`}
        onClick={onToggle}
        aria-pressed={upvoted}
        title={upvoted ? 'Upvote entfernen' : 'Upvoten'}
      >
        <span className="fb-upvote__arrow">▲</span>
        <span className="fb-upvote__count">{item.upvotes}</span>
      </button>
      <div className="fb-card__body">
        <div className="fb-card__head">
          {categoryChip(item.category)}
          {inShortlist && <span className="fb-shortlist-chip">⭐ Shortlist</span>}
          <span className="fb-card__time">{timeAgo(item.createdAt)}</span>
        </div>
        <h3 className="fb-card__title">{item.title}</h3>
        <p className="fb-card__desc">{item.body}</p>
        <div className="fb-card__author">— {item.authorName}</div>
      </div>
    </div>
  );
}
